import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useCatalog, useFlights, useProjectFile, useProjects } from "../../api/hooks";
import type { LaneNode, LanesResponse, OverlayResponse, SnapshotDict } from "../../api/types";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, JsonTree, Modal, Spinner, errText, fmtBytes, fmtTick } from "../../components/ui";
import { TriggerGraphView } from "../../graph/TriggerGraphView";
import type { GraphNode, GraphOverlay } from "../../graph/types";
import { useTriggerGraph } from "../../graph/useTriggerGraph";
import { LanesView } from "../analysis/LanesView";
import { AXIS_W, ParamChart, type ChartDecorations } from "../analysis/ParamChart";
import { triggerColor, triggerColorMap } from "../analysis/triggerColors";
import "../analysis/analysis.css";
import {
  fetchLiveOverlay,
  liveKeys,
  useDeleteBinding,
  useEndSession,
  useForgetSession,
  useLiveBindings,
  useLiveEvents,
  useLiveLanes,
  useLiveSeries,
  useLiveSession,
  useLiveSessions,
  useLiveStatus,
  usePutBinding,
  useStartDemo,
  useStopDemo,
  type LiveEvent,
  type LiveSessionSummary,
  type SessionStatus,
} from "./liveApi";
import "./live.css";

const PALETTE = ["#60a5fa", "#f472b6", "#34d399", "#fbbf24", "#a78bfa", "#fb923c", "#22d3ee", "#e879f9", "#a3e635", "#f87171"];

function projectRelative(file: string | null): string | null {
  if (!file) return null;
  const norm = file.replace(/\\/g, "/");
  if (!/^([a-zA-Z]:)?\//.test(norm)) return norm;
  const m = norm.match(/\/(triggers|derived|renderers)\/[^/]+$/);
  return m ? norm.slice(m.index! + 1) : norm.split("/").slice(-2).join("/");
}

const STATUS_DOT: Record<SessionStatus, string> = {
  active: "running",
  finishing: "queued",
  waiting: "queued",
  idle: "done",
  unbound: "failed",
  done: "done",
  failed: "failed",
  discarded: "failed",
};

interface Feed {
  kind: "snapshot" | "artifact" | "event" | "log" | "segment";
  t?: number;
  text: string;
  level?: string;
  ts?: string;
}

export function LivePage() {
  const session = useSession();
  const { projectId, theme, liveKey, setLiveKey, liveParams: params, setLiveParams: setParams, toast } = session;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const status = useLiveStatus();
  const sessions = useLiveSessions();
  const detail = useLiveSession(liveKey);
  const catalog = useCatalog(detail.data?.project_id ?? projectId);
  const endSession = useEndSession();
  const forget = useForgetSession();
  const stopDemo = useStopDemo();

  const [triggerFilter, setTriggerFilter] = useState<string | null>(null);
  const [paramQuery, setParamQuery] = useState("");
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [pinned, setPinned] = useState<number | null>(null);
  const [range, setRange] = useState<[number, number] | null>(null);
  const [box, setBox] = useState({ left: AXIS_W, width: 600 });
  const [overlay, setOverlay] = useState<(OverlayResponse & { trace: boolean }) | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [inspect, setInspect] = useState<GraphNode | LaneNode | null>(null);
  const [showLanes, setShowLanes] = useState(true);
  const [follow, setFollow] = useState(true);
  const [liveT, setLiveT] = useState<number>(-1);
  const [feed, setFeed] = useState<Feed[]>([]);
  const [tab, setTab] = useState<"feed" | "triggers" | "files" | "flights">("feed");
  const [demoOpen, setDemoOpen] = useState(false);
  const [bindOpen, setBindOpen] = useState(false);

  const disabled = status.isError && (status.error as { status?: number } | null)?.status === 404;

  // pick a session automatically: prefer the active one. The remembered key can outlive the
  // session (server restart, forget, expiry) -- drop it rather than query a session that is gone.
  const list = sessions.data?.sessions ?? [];
  const detailMissing = detail.isError && (detail.error as { status?: number } | null)?.status === 404;
  useEffect(() => {
    if (!sessions.data) return;
    if (liveKey && list.some((s) => s.key === liveKey)) return;
    // a key missing from the list may just be newer than the list (demo just started): only let
    // go once the server itself says the session does not exist
    if (liveKey && !detailMissing) return;
    const pick = list.find((s) => s.status === "active") ?? list[0] ?? null;
    if ((pick?.key ?? null) !== liveKey) setLiveKey(pick?.key ?? null);
  }, [sessions.data, list, liveKey, detailMissing, setLiveKey]);

  const series = useLiveSeries(liveKey, params, 1500);
  const lanes = useLiveLanes(liveKey, triggerFilter);
  const total = Math.max(series.data?.total_seconds ?? 0, lanes.data?.total_seconds ?? 0, liveT + 1, 1);

  // -- WebSocket: refresh queries on events (throttled) ----------------------------------------
  const lastSeries = useRef(0);
  const lastDetail = useRef(0);
  const throttled = useCallback(
    (ref: React.MutableRefObject<number>, ms: number, fn: () => void) => {
      const now = performance.now();
      if (now - ref.current >= ms) {
        ref.current = now;
        fn();
      }
    },
    [],
  );
  const push = useCallback((f: Feed) => setFeed((prev) => [...prev.slice(-299), f]), []);

  const onEvent = useCallback(
    (e: LiveEvent) => {
      switch (e.type) {
        case "session":
        case "segment_start":
        case "segment_end":
          qc.setQueryData<{ sessions: LiveSessionSummary[] }>(liveKeys.sessions, (old) => {
            if (!e.session) return old;
            const arr = old?.sessions ?? [];
            const i = arr.findIndex((s) => s.key === e.session!.key);
            const next = i >= 0 ? arr.map((s, j) => (j === i ? e.session! : s)) : [...arr, e.session];
            return { sessions: next };
          });
          if (e.key === liveKey) {
            qc.invalidateQueries({ queryKey: liveKeys.session(liveKey) });
            if (e.type === "segment_start") {
              setFeed([]);
              setPinned(null);
              push({ kind: "segment", text: `flight ${e.segment?.flight_id} started${e.resumed ? " (resumed from checkpoint)" : ""}`, ts: e.ts });
            } else if (e.type === "segment_end") {
              push({
                kind: "segment",
                text: `flight ${e.segment?.flight_id} ${e.segment?.status} (${e.segment?.reason}) · ${e.segment?.fires} fire(s) · ${e.segment?.artifacts} file(s)`,
                ts: e.ts,
                level: e.segment?.status === "failed" ? "error" : undefined,
              });
              qc.invalidateQueries({ queryKey: ["runs"] });
              qc.invalidateQueries({ queryKey: ["flights"] });
            }
          }
          break;
        case "frame":
          if (e.key === liveKey && typeof e.t === "number") {
            setLiveT(e.t);
            throttled(lastSeries, 700, () => {
              qc.invalidateQueries({ queryKey: ["live", "series", liveKey] });
              qc.invalidateQueries({ queryKey: ["live", "lanes", liveKey] });
            });
            throttled(lastDetail, 3000, () => qc.invalidateQueries({ queryKey: liveKeys.session(liveKey) }));
          }
          break;
        case "snapshot":
          if (e.key === liveKey && e.snapshot) {
            push({ kind: "snapshot", t: e.snapshot.t, text: `${e.snapshot.trigger} → ${e.snapshot.label}`, ts: e.ts });
            qc.invalidateQueries({ queryKey: liveKeys.session(liveKey) });
            qc.invalidateQueries({ queryKey: ["live", "lanes", liveKey] });
          }
          break;
        case "artifact":
          if (e.key === liveKey && e.artifact) {
            push({ kind: "artifact", t: e.t, text: `${e.artifact.report}/${e.artifact.output}: ${e.artifact.filename} (${fmtBytes(e.artifact.size)})`, ts: e.ts });
            qc.invalidateQueries({ queryKey: liveKeys.session(liveKey) });
          }
          break;
        case "event":
          if (e.key === liveKey && e.event) push({ kind: "event", t: e.event.t, text: `${e.event.trigger} ${e.event.kind}: ${e.event.message}`, level: e.event.kind === "error" ? "error" : undefined, ts: e.ts });
          break;
        case "log":
          if (!e.key || e.key === liveKey) push({ kind: "log", text: e.message ?? "", level: e.level, ts: e.ts });
          break;
        case "demo":
        case "transport":
          qc.invalidateQueries({ queryKey: liveKeys.status });
          break;
        default:
          break;
      }
    },
    [liveKey, qc, push, throttled],
  );
  const socket = useLiveEvents({ onEvent, replay: 100, enabled: !disabled });

  // reset per session
  useEffect(() => {
    setFeed([]);
    setPinned(null);
    setOverlay(null);
    setLiveT(detail.data?.t ?? -1);
  }, [liveKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (detail.data && detail.data.t > liveT) setLiveT(detail.data.t);
  }, [detail.data, liveT]);

  // -- graph + overlay ----------------------------------------------------------------------------
  const triggerNames = useMemo(() => {
    const s = new Set<string>();
    for (const t of detail.data?.triggers ?? []) s.add(t.name);
    for (const t of catalog.data?.triggers ?? []) s.add(t.name);
    return [...s];
  }, [detail.data, catalog.data]);
  const trigColors = useMemo(() => triggerColorMap(triggerNames), [triggerNames]);
  const colorOf = useCallback((trigger: string) => triggerColor(trigger, trigColors), [trigColors]);
  const graphTrigger = triggerFilter ?? triggerNames[0] ?? null;
  const graphProject = detail.data?.project_id ?? projectId;
  const triggerFile = projectRelative(catalog.data?.triggers.find((t) => t.name === graphTrigger)?.file ?? lanes.data?.nodes.find((n) => n.trigger === graphTrigger)?.file ?? null);
  const file = useProjectFile(graphProject, triggerFile);
  const graphQ = useTriggerGraph(file.data?.content ?? "", triggerFile ?? "scratch.py", 0);
  const graph = file.data ? graphQ.data ?? null : null;

  const effT = pinned ?? hoverT ?? (follow ? liveT : null);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!liveKey || effT == null || effT < 0) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      try {
        setOverlay(await fetchLiveOverlay(liveKey, Math.round(effT), graphTrigger));
      } catch {
        /* session may have ended */
      }
    }, pinned != null || hoverT != null ? 120 : 400);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [liveKey, effT, graphTrigger, pinned, hoverT]);

  const graphOverlay: GraphOverlay | undefined = useMemo(() => {
    if (!overlay) return undefined;
    const out: GraphOverlay = {};
    for (const [k, v] of Object.entries(overlay.overlay)) out[k] = Array.isArray(v) ? `[${v.length}]` : v;
    return out;
  }, [overlay]);

  const snapshots: SnapshotDict[] = useMemo(() => (detail.data?.snapshots ?? []).filter((s) => !triggerFilter || s.trigger === triggerFilter), [detail.data, triggerFilter]);
  const deco: ChartDecorations = useMemo(
    () => ({
      markers: liveT >= 0 && !pinned ? [{ t: liveT, label: "now", color: theme === "dark" ? "rgba(229,231,235,0.7)" : "rgba(17,24,39,0.6)" }] : [],
      windows: snapshots.map((f) => ({ start: f.window.start, end: f.window.end, label: f.label, color: colorOf(f.trigger) })),
      pinned,
      range,
    }),
    [snapshots, pinned, range, liveT, theme, colorOf],
  );

  const onHover = useCallback((t: number | null) => setHoverT(t == null ? null : Math.round(t)), []);
  const onPin = useCallback((t: number) => setPinned((p) => (p != null && Math.abs(p - t) < 0.5 ? null : Math.round(t))), []);

  const mnemonics = useMemo(() => {
    const list = [...(detail.data?.mnemonics ?? []), ...(catalog.data?.derived ?? [])];
    return list.length ? list : catalog.data?.parameters.map((p) => p.mnemonic) ?? [];
  }, [detail.data, catalog.data]);
  const paramOptions = useMemo(() => {
    const q = paramQuery.toLowerCase();
    return mnemonics.filter((m) => !params.includes(m) && (!q || m.toLowerCase().includes(q))).slice(0, 40);
  }, [mnemonics, params, paramQuery]);
  const addParam = (m: string) => {
    if (!m || params.includes(m)) return;
    setParams([...params, m]);
    setParamQuery("");
  };

  const lanesForView: LanesResponse | null = lanes.data
    ? { ...lanes.data, run_id: lanes.data.run_id ?? "", flight_id: lanes.data.flight_id ?? "", total_seconds: lanes.data.total_seconds }
    : null;

  if (disabled) return <Empty title="Live streaming is disabled on this server">Set REPORTS_STUDIO_LIVE_ENABLED=true (and REPORTS_STUDIO_NATS_URL) to enable it.</Empty>;

  const cur = detail.data ?? list.find((s) => s.key === liveKey) ?? null;
  const seg = cur?.segment ?? null;
  const tr = status.data?.transport;

  return (
    <>
    <Split id="live" className="live" sizes={["1fr", 400]} minSizes={[360, 260]}>
      <div className="pane">
        <div className="pane-head row wrap">
          <span className={`live-pill ${socket.state}`} title={socket.lastError ?? `events socket ${socket.state}`}>
            ● {socket.state === "open" ? "live" : socket.state}
          </span>
          <span
            className={`badge ${tr?.kind === "nats" ? (tr.connected ? "ok" : "err") : ""}`}
            title={tr?.kind === "nats" ? `${tr.url} ${tr.subject} · ${tr.messages ?? 0} msg · ${tr.mode}${tr.last_error ? `\n${tr.last_error}` : ""}` : tr?.reason ?? "no NATS configured"}
          >
            {tr?.kind === "nats" ? `NATS ${tr.connected ? "connected" : "disconnected"}` : "NATS off"}
          </span>
          {status.data?.demos.some((d) => d.running) ? (
            <>
              <span className="badge info">demo running</span>
              <button
                className="btn danger sm"
                onClick={() => stopDemo.mutate(undefined, { onSuccess: (r) => toast(`stopped ${r.stopped} demo stream(s)`, "success"), onError: (e) => toast(errText(e), "error") })}
                disabled={stopDemo.isPending}
                title="Stop publishing the demo replay. The open flight stays active until you press End flight (or it times out)."
              >
                ■ Stop demo
              </button>
            </>
          ) : null}
          <span className="hdr-sep" />
          <label className="small dim">Session</label>
          <select className="inp sm" value={liveKey ?? ""} onChange={(e) => setLiveKey(e.target.value || null)} style={{ maxWidth: 320 }}>
            {!list.length ? <option value="">(no devices seen yet)</option> : null}
            {list.map((s) => (
              <option key={s.key} value={s.key}>
                {s.status === "active" ? "● " : s.status === "waiting" ? "◔ " : "○ "}
                {s.tail ?? s.device_id} · {s.device_id}/{s.session_id} · {s.status}
                {s.project_name ? ` · ${s.project_name}` : s.bound ? "" : " · unbound"}
              </option>
            ))}
          </select>
          {cur && (cur.status === "active" || cur.status === "waiting") ? (
            <button
              className="btn sm"
              onClick={() => endSession.mutate({ key: cur.key, reason: "ended from UI" })}
              disabled={endSession.isPending}
              title="Close the open flight now: end-of-flight triggers run and reports are rendered"
            >
              ■ End flight
            </button>
          ) : null}
          {cur && cur.status !== "active" && cur.status !== "waiting" && cur.status !== "finishing" ? (
            <button className="btn ghost sm" onClick={() => forget.mutate(cur.key, { onSuccess: () => setLiveKey(null) })} title="Forget this device session">
              ✕ forget
            </button>
          ) : null}
          <label className="small dim">Trigger</label>
          <select className="inp sm" value={triggerFilter ?? ""} onChange={(e) => (setTriggerFilter(e.target.value || null), setSelectedNode(null))}>
            <option value="">all</option>
            {triggerNames.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          <span className="spacer" />
          <button className="btn sm" onClick={() => setBindOpen(true)} title="Which project evaluates which device">
            ⇄ Bindings{status.data ? ` (${status.data.bindings.length})` : ""}
          </button>
          <button className="btn sm primary" onClick={() => setDemoOpen(true)} title="Replay a recorded flight as a live stream">
            ▶ Demo
          </button>
        </div>

        {cur ? (
          <div className="live-strip row wrap mono small">
            <span className={`status-dot ${STATUS_DOT[cur.status] ?? "done"}`} />
            <b>{cur.tail ?? cur.device_id}</b>
            <span className="dim">{cur.device_id}/{cur.session_id}</span>
            <span className={`badge ${cur.status === "active" ? "ok" : cur.status === "unbound" ? "err" : cur.status === "waiting" ? "warn" : ""}`}>{cur.status}</span>
            {cur.project_name ? <span className="dim">→ {cur.project_name}</span> : !cur.bound ? <span className="badge err">no binding for {cur.device_id}</span> : null}
            <span className="hdr-sep" />
            {seg ? (
              <>
                <span title="flight id">{seg.flight_id}</span>
                <span title="flight time">t={fmtTick(Math.max(liveT, 0))}</span>
                <span title="frames received in this flight">{seg.frames} fr</span>
                <span title="fires">{seg.fires} fire{seg.fires === 1 ? "" : "s"}</span>
                <span title="rendered files">{seg.artifacts} file{seg.artifacts === 1 ? "" : "s"}</span>
                {seg.resumed ? <span className="badge info">resumed</span> : null}
                {seg.checkpoints ? <span className="dim" title="checkpoints written">ckpt×{seg.checkpoints}</span> : null}
                {seg.errors.length ? <span className="badge err" title={seg.errors.join("\n")}>{seg.errors.length} error(s)</span> : null}
              </>
            ) : cur.status === "waiting" ? (
              <span className="dim">waiting for flight_start: {detail.data?.live?.flight_start}</span>
            ) : (
              <span className="dim">no open flight</span>
            )}
            <span className="spacer" />
            <span className="dim" title="messages · rate · latency · gaps">
              {cur.stats.messages} msg
              {cur.stats.rate_hz != null ? ` · ${cur.stats.rate_hz.toFixed(1)} fr/s` : ""}
              {cur.stats.latency_ms != null ? ` · ${Math.round(cur.stats.latency_ms)} ms` : ""}
              {cur.stats.wps ? ` · ${cur.stats.wps} wps` : ""}
              {cur.stats.gaps ? ` · ${cur.stats.gaps} gap(s)/${cur.stats.missing_frames} fr` : ""}
              {cur.stats.seq_resets ? ` · ${cur.stats.seq_resets} reset(s)` : ""}
            </span>
          </div>
        ) : null}

        <div className="row wrap param-bar">
          {params.map((m, i) => (
            <span key={m} className="chip on" style={{ borderColor: PALETTE[i % PALETTE.length] }}>
              {m}
              <button className="x" onClick={() => setParams(params.filter((x) => x !== m))}>
                ✕
              </button>
            </span>
          ))}
          <span className="param-add">
            <input
              className="inp sm mono"
              placeholder="＋ parameter…"
              value={paramQuery}
              onChange={(e) => setParamQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") addParam(paramOptions[0] ?? paramQuery.trim());
                if (e.key === "Escape") setParamQuery("");
              }}
            />
            {paramQuery ? (
              <div className="param-menu">
                {paramOptions.map((m) => (
                  <div key={m} className="mono" onMouseDown={() => addParam(m)}>
                    {m}
                  </div>
                ))}
                {!paramOptions.length ? <div className="dim">no match</div> : null}
              </div>
            ) : null}
          </span>
          {series.data?.missing.length ? <span className="badge warn">missing: {series.data.missing.join(", ")}</span> : null}
          {snapshots.length ? (
            <span className="dim small">
              {snapshots.map((f) => (
                <button
                  key={f.id}
                  className="btn ghost sm mono snap-chip"
                  style={{ "--snap": colorOf(f.trigger) } as CSSProperties}
                  onClick={() => setPinned(f.window.start)}
                  title={`${f.trigger}: window ${fmtTick(f.window.start)}–${fmtTick(f.window.end)} (${f.window.end - f.window.start + 1}s) · emitted at ${fmtTick(f.t)}`}
                >
                  {f.label} {fmtTick(f.window.start)}–{fmtTick(f.window.end)}
                </button>
              ))}
            </span>
          ) : null}
          <span className="spacer" />
          <span className="mono small">
            {hoverT != null ? `t=${fmtTick(hoverT)}` : ""}
            {pinned != null ? (
              <span className="badge info" onClick={() => setPinned(null)} style={{ cursor: "pointer", marginLeft: 6 }} title="unpin">
                pin {fmtTick(pinned)} ✕
              </span>
            ) : null}
          </span>
          <label className="row small dim" style={{ gap: 4 }} title="Keep the graph overlay on the latest second">
            <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> follow
          </label>
          <label className="row small dim" style={{ gap: 4 }}>
            <input type="checkbox" checked={showLanes} onChange={(e) => setShowLanes(e.target.checked)} /> lanes
          </label>
        </div>

        <div className="charts" onMouseLeave={() => setHoverT(null)}>
          {!liveKey ? (
            <Empty title="No live sessions">
              Devices publishing on <code>stream.raw.&lt;device&gt;.&lt;session&gt;</code> appear here as soon as the first frame arrives. Bind a device pattern to a project
              (Bindings) so its triggers are evaluated, or start a <b>Demo</b> to replay a recorded flight.
            </Empty>
          ) : !params.length ? (
            <Empty title="Add parameters to plot">Type a mnemonic above. The chart grows as frames arrive; fires and snapshot windows are drawn as they happen.</Empty>
          ) : null}
          {series.error && liveKey ? <div className="problem err">{errText(series.error)}</div> : null}
          {params.map((m, i) => {
            const tile = series.data?.series[m];
            if (!tile) return null;
            return (
              <ParamChart
                key={`${liveKey}:${m}`}
                tile={tile}
                total={total}
                color={PALETTE[i % PALETTE.length]}
                theme={theme}
                deco={deco}
                onHover={onHover}
                onPin={onPin}
                onPlotBox={i === 0 ? setBox : undefined}
                onRemove={() => setParams(params.filter((x) => x !== m))}
              />
            );
          })}
          {showLanes && lanesForView && lanesForView.nodes.length ? (
            <LanesView
              lanes={lanesForView}
              total={total}
              box={box}
              hoverT={hoverT}
              pinned={pinned}
              selectedNodeId={selectedNode}
              onSelectNode={(n) => (setSelectedNode(n.id), setInspect(n))}
              onHoverInterval={setRange}
              onPin={onPin}
              triggerFilter={triggerFilter}
              triggerColors={trigColors}
              windows={snapshots.map((f) => ({ trigger: f.trigger, label: f.label, start: f.window.start, end: f.window.end, color: colorOf(f.trigger) }))}
            />
          ) : showLanes && liveKey && seg ? (
            <div className="dim small" style={{ padding: 8 }}>
              No boolean lanes yet {detail.data?.live?.trace === "off" ? "(live.trace is off for this project)" : ""}.
            </div>
          ) : null}
        </div>
      </div>

      <div className="pane">
        <div className="pane-head">
          <span>⚡ {graphTrigger ?? "graph"}</span>
          {triggerFile ? (
            <button className="btn ghost sm mono" onClick={() => (session.setOpenTriggerFile(triggerFile), navigate("/triggers"))} title="open in trigger workbench">
              {triggerFile.split("/").pop()} ↗
            </button>
          ) : null}
          <span className="spacer" />
          {effT != null && effT >= 0 ? <span className={`badge${follow && pinned == null && hoverT == null ? " ok" : ""}`}>{fmtTick(effT)}</span> : null}
        </div>
        <Split id="live-side" className="fill" direction="column" sizes={["1fr", 200, 220]} minSizes={[140, 70, 90]}>
        <div className="graph-box">
          {graph ? (
            <TriggerGraphView graph={graph} selectedNodeId={selectedNode} overlay={graphOverlay} minimap={false} onNodeClick={(n) => (setSelectedNode(n.id), setInspect(n))} />
          ) : file.isLoading ? (
            <Spinner label="loading trigger" />
          ) : (
            <Empty title="No trigger graph">{cur && !cur.bound ? "Bind this device to a project first." : triggerNames.length ? "Pick a trigger." : "The project has no triggers."}</Empty>
          )}
        </div>
        <div className="whynot">
          <div className="section-title row">
            Why not? <span className="faint">– false conditions at {effT != null && effT >= 0 ? fmtTick(effT) : "cursor"}</span>
          </div>
          {!seg ? (
            <div className="dim small">Needs an open flight.</div>
          ) : !overlay ? (
            <div className="dim small">Waiting for the first evaluated second…</div>
          ) : !overlay.trace ? (
            <div className="dim small">Tracing is off for this flight (project live.trace or binding).</div>
          ) : !overlay.why_not.length ? (
            <div className="small">
              <span className="badge ok">all conditions true</span> <span className="dim">(or nothing evaluated at this second)</span>
            </div>
          ) : (
            <div className="whynot-list">
              {overlay.why_not.map((w) => (
                <div
                  key={`${w.id}:${w.occurrence}`}
                  className={`whynot-item${w.id === selectedNode ? " sel" : ""}`}
                  onClick={() => {
                    setSelectedNode(w.id);
                    const gn = graph?.nodes.find((n) => n.id === w.id);
                    if (gn) setInspect(gn);
                  }}
                  title={`${w.kind} · line ${w.line}`}
                >
                  <span className="badge">{w.kind}</span>
                  <span className="mono">{w.label}</span>
                  <span className="faint small">L{w.line}</span>
                </div>
              ))}
            </div>
          )}
          {inspect ? (
            <details className="inspect" open>
              <summary className="small">
                {"text" in inspect && inspect.text ? (
                  <>
                    <strong>{inspect.text}</strong> <span className="mono faint">{inspect.label}</span>
                  </>
                ) : (
                  <span className="mono">
                    {inspect.kind} · {inspect.label}
                  </span>
                )}
                <button className="btn ghost sm" style={{ float: "right" }} onClick={() => (setInspect(null), setSelectedNode(null))}>
                  ✕
                </button>
              </summary>
              <div className="small dim">
                value @ t: <span className="mono">{overlay ? String(overlay.overlay[inspect.id] ?? "—") : "—"}</span>
              </div>
              {"params" in inspect && inspect.params && Object.keys(inspect.params).length ? <JsonTree value={inspect.params} name="params" /> : null}
            </details>
          ) : null}
        </div>
        <div className="live-tabs">
          <div className="dock-tabs">
            <button className={tab === "feed" ? "on" : ""} onClick={() => setTab("feed")}>
              Feed{feed.length ? <span className="badge">{feed.length}</span> : null}
            </button>
            <button className={tab === "triggers" ? "on" : ""} onClick={() => setTab("triggers")}>
              Triggers
            </button>
            <button className={tab === "files" ? "on" : ""} onClick={() => setTab("files")}>
              Files{detail.data?.artifacts.length ? <span className="badge">{detail.data.artifacts.length}</span> : null}
            </button>
            <button className={tab === "flights" ? "on" : ""} onClick={() => setTab("flights")}>
              Flights{detail.data?.history.length ? <span className="badge">{detail.data.history.length}</span> : null}
            </button>
          </div>
          <div className="live-tab-body mono small">
            {tab === "feed" ? (
              feed.length ? (
                [...feed].reverse().map((f, i) => (
                  <div key={i} className={`feed-row ${f.kind}${f.level === "error" ? " sev-error" : f.level === "warning" ? " sev-warning" : ""}`}>
                    <span className="faint">{f.ts ? f.ts.slice(11, 19) : ""}</span>
                    {f.t != null ? <span className="dim">{fmtTick(f.t)}</span> : <span />}
                    <span className={`badge ${f.kind === "snapshot" ? "ok" : f.kind === "artifact" ? "info" : ""}`}>{f.kind}</span>
                    <span>{f.text}</span>
                  </div>
                ))
              ) : (
                <span className="dim">Fires, files, trigger events and log lines appear here as they happen.</span>
              )
            ) : null}
            {tab === "triggers" ? (
              detail.data?.triggers.length ? (
                detail.data.triggers.map((t) => (
                  <div key={t.name} className="feed-row" onClick={() => setTriggerFilter(t.name)} style={{ cursor: "pointer" }}>
                    <span className={`status-dot ${t.failed ? "failed" : t.armed ? "running" : "done"}`} title={t.failed ? "failed" : t.armed ? "armed" : "disarmed"} />
                    <span>{t.name}</span>
                    <span className="dim">{t.fires} fire(s)</span>
                    {t.end_of_flight ? <span className="badge">end-of-flight</span> : null}
                    {t.reports.length ? <span className="faint">→ {t.reports.join(", ")}</span> : null}
                  </div>
                ))
              ) : (
                <span className="dim">No triggers loaded (no open flight or unbound device).</span>
              )
            ) : null}
            {tab === "files" ? (
              detail.data?.artifacts.length ? (
                detail.data.artifacts.map((a, i) => (
                  <div key={i} className="feed-row">
                    <span className="dim">{a.label}</span>
                    {seg?.run_id && a.filename ? (
                      <a href={`/api/runs/${seg.run_id}/flights/${encodeURIComponent(seg.flight_id)}/artifacts/${encodeURIComponent(a.filename)}`} target="_blank" rel="noreferrer">
                        {a.filename}
                      </a>
                    ) : (
                      <span>{a.filename}</span>
                    )}
                    <span className="faint">{fmtBytes(a.size)}</span>
                    {a.deliveries ? <span className="badge info">{a.deliveries} delivery</span> : null}
                  </div>
                ))
              ) : (
                <span className="dim">No files rendered in this flight yet.</span>
              )
            ) : null}
            {tab === "flights" ? (
              detail.data?.history.length ? (
                [...detail.data.history].reverse().map((h, i) => (
                  <div key={i} className="feed-row">
                    <span className={`status-dot ${h.status === "done" ? "done" : "failed"}`} />
                    <span>{h.flight_id}</span>
                    <span className="dim">
                      {h.status} ({h.reason}) · {fmtTick(h.ticks)} · {h.fires} fire(s) · {h.artifacts} file(s)
                    </span>
                    {h.run_id ? (
                      <button className="btn ghost sm" onClick={() => (session.setCurrentRun(h.run_id!), navigate("/runs"))}>
                        run ↗
                      </button>
                    ) : null}
                  </div>
                ))
              ) : (
                <span className="dim">Finished flights of this device appear here (they also show up in Flights and Runs).</span>
              )
            ) : null}
          </div>
        </div>
        </Split>
      </div>
    </Split>
      {demoOpen ? <DemoDialog onClose={() => setDemoOpen(false)} /> : null}
      {bindOpen ? <BindingsDialog onClose={() => setBindOpen(false)} /> : null}
    </>
  );
}

// -- demo dialog ---------------------------------------------------------------------------------
function DemoDialog({ onClose }: { onClose: () => void }) {
  const { projectId, toast, setLiveKey } = useSession();
  const flights = useFlights();
  const status = useLiveStatus();
  const start = useStartDemo();
  const stop = useStopDemo();
  const [flightId, setFlightId] = useState<string>("");
  const [path, setPath] = useState("");
  const [device, setDevice] = useState("DEMO-001");
  const [sessionId, setSessionId] = useState("");
  const [speed, setSpeed] = useState(16);
  const [loop, setLoop] = useState(false);
  const [bind, setBind] = useState(true);
  const [tail, setTail] = useState("");

  useEffect(() => {
    if (!flightId && flights.data?.length) setFlightId(flights.data[0].id);
  }, [flights.data, flightId]);
  const flight = flights.data?.find((f) => f.id === flightId);

  const go = async () => {
    try {
      const st = await start.mutateAsync({
        flight_id: path ? undefined : flightId || undefined,
        path: path || undefined,
        device_id: device.trim() || "DEMO-001",
        session_id: sessionId.trim() || undefined,
        speed,
        loop,
        project_id: bind && projectId ? projectId : undefined,
        tail: tail.trim() || flight?.tail || undefined,
      });
      setLiveKey(`${st.device_id}:${st.session_id}`);
      toast(`demo started on ${st.subject} (${st.frames} frames @ ${st.speed}x)`, "success");
      onClose();
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  return (
    <Modal
      title="Demo stream"
      onClose={onClose}
      width={520}
      footer={
        <>
          <span className="dim small">Replays a recorded flight through the same consumer path as NATS (demo_mode headers).</span>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={go} disabled={start.isPending || (!flightId && !path)}>
            {start.isPending ? <Spinner /> : "▶ Start"}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <label>Flight</label>
        <select className="inp" value={flightId} onChange={(e) => setFlightId(e.target.value)} disabled={!!path}>
          {(flights.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.tail ? `${f.tail} · ` : ""}
              {f.date ? `${f.date} · ` : ""}
              {f.id}
            </option>
          ))}
        </select>
        <label>or server path</label>
        <input className="inp mono" value={path} onChange={(e) => setPath(e.target.value)} placeholder="D:\flights\XA-HPY\68B6FEE8 (folder of .tsc)" />
        <label>Device id</label>
        <input className="inp mono" value={device} onChange={(e) => setDevice(e.target.value)} />
        <label>Session id</label>
        <input className="inp mono" value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="(auto)" />
        <label>Speed</label>
        <span className="row">
          <input className="inp" type="number" min={0} step={1} value={speed} onChange={(e) => setSpeed(Number(e.target.value))} style={{ width: 90 }} />
          <span className="dim small">× real time (0 = as fast as possible; 1 = one frame every 4 s)</span>
        </span>
        <label>Loop</label>
        <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
        <label>Bind to project</label>
        <span className="row">
          <input type="checkbox" checked={bind} onChange={(e) => setBind(e.target.checked)} disabled={!projectId} />
          <span className="dim small">{projectId ? `bind ${device} → ${projectId}` : "select a project first"}</span>
        </span>
        <label>Tail override</label>
        <input className="inp mono" value={tail} onChange={(e) => setTail(e.target.value)} placeholder={flight?.tail ?? "from flight"} />
      </div>
      {status.data?.demos.length ? (
        <div className="section-title" style={{ marginTop: 10 }}>
          Running demos
        </div>
      ) : null}
      {(status.data?.demos ?? []).map((d) => (
        <div key={d.device_id} className="row small mono" style={{ gap: 8 }}>
          <span className={`status-dot ${d.running ? "running" : "done"}`} />
          <span>{d.subject}</span>
          <span className="dim">
            {d.published}/{d.frames} frames{d.loop ? ` · loop ${d.loops}` : ""} · {d.speed}x
          </span>
          {d.error ? <span className="badge err">{d.error}</span> : null}
          <span className="spacer" />
          {d.running ? (
            <button className="btn ghost sm" onClick={() => stop.mutate(d.device_id)}>
              ■ stop
            </button>
          ) : null}
        </div>
      ))}
    </Modal>
  );
}

// -- bindings dialog ---------------------------------------------------------------------------------
function BindingsDialog({ onClose }: { onClose: () => void }) {
  const { projectId, toast } = useSession();
  const projects = useProjects();
  const bindings = useLiveBindings();
  const put = usePutBinding();
  const del = useDeleteBinding();
  const [device, setDevice] = useState("*");
  const [proj, setProj] = useState(projectId ?? "");
  const [tail, setTail] = useState("");
  const [label, setLabel] = useState("");
  const [deliver, setDeliver] = useState<"" | "true" | "false">("");
  const [trace, setTrace] = useState<"" | "off" | "bool" | "all">("");
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    if (!proj && projects.data?.length) setProj(projectId ?? projects.data[0].id);
  }, [projects.data, proj, projectId]);

  const save = async () => {
    try {
      await put.mutateAsync({
        id: editing ?? undefined,
        device: device.trim() || "*",
        project_id: proj,
        tail: tail.trim() || null,
        label,
        enabled: true,
        deliver: deliver === "" ? null : deliver === "true",
        trace: trace || null,
      });
      toast(`binding ${device} → ${proj} saved`, "success");
      setEditing(null);
      setDevice("*");
      setTail("");
      setLabel("");
    } catch (e) {
      toast(errText(e), "error");
    }
  };

  return (
    <Modal
      title="Device bindings"
      onClose={onClose}
      width={640}
      footer={
        <>
          <span className="dim small">
            Streams carry only a <code>device_id</code>; a binding says which project (frame + triggers + reports) evaluates it. Globs allowed, most specific wins,{" "}
            <code>*</code> is the default.
          </span>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <table className="tbl small">
        <thead>
          <tr>
            <th>Device pattern</th>
            <th>Project</th>
            <th>Tail</th>
            <th>Deliver</th>
            <th>Trace</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {(bindings.data?.bindings ?? []).map((b) => (
            <tr key={b.id} className={editing === b.id ? "sel" : ""}>
              <td className="mono">
                {b.device}
                {!b.enabled ? <span className="badge warn">disabled</span> : null}
              </td>
              <td>{b.project_id}</td>
              <td className="mono">{b.tail ?? <span className="faint">device id</span>}</td>
              <td>{b.deliver == null ? <span className="faint">project</span> : b.deliver ? "yes" : "no"}</td>
              <td>{b.trace ?? <span className="faint">project</span>}</td>
              <td className="row" style={{ gap: 4 }}>
                <button
                  className="btn ghost sm"
                  onClick={() => {
                    setEditing(b.id);
                    setDevice(b.device);
                    setProj(b.project_id);
                    setTail(b.tail ?? "");
                    setLabel(b.label);
                    setDeliver(b.deliver == null ? "" : b.deliver ? "true" : "false");
                    setTrace((b.trace as "off" | "bool" | "all" | null) ?? "");
                  }}
                >
                  edit
                </button>
                <button className="btn ghost sm" onClick={() => del.mutate(b.id)}>
                  ✕
                </button>
              </td>
            </tr>
          ))}
          {!bindings.data?.bindings.length ? (
            <tr>
              <td colSpan={6} className="dim">
                No bindings yet: devices are tracked but not evaluated.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      <div className="section-title" style={{ marginTop: 10 }}>
        {editing ? `Edit binding ${editing}` : "Add binding"}
      </div>
      <div className="form-grid">
        <label>Device pattern</label>
        <input className="inp mono" value={device} onChange={(e) => setDevice(e.target.value)} placeholder="AV-573-* or *" />
        <label>Project</label>
        <select className="inp" value={proj} onChange={(e) => setProj(e.target.value)}>
          {(projects.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <label>Tail</label>
        <input className="inp mono" value={tail} onChange={(e) => setTail(e.target.value)} placeholder="(defaults to device id)" />
        <label>Label</label>
        <input className="inp" value={label} onChange={(e) => setLabel(e.target.value)} />
        <label>Deliver</label>
        <select className="inp" value={deliver} onChange={(e) => setDeliver(e.target.value as typeof deliver)}>
          <option value="">project default</option>
          <option value="true">route through subscriptions</option>
          <option value="false">never deliver</option>
        </select>
        <label>Trace</label>
        <select className="inp" value={trace} onChange={(e) => setTrace(e.target.value as typeof trace)}>
          <option value="">project default</option>
          <option value="bool">bool (lanes + why-not)</option>
          <option value="all">all (values too)</option>
          <option value="off">off</option>
        </select>
      </div>
      <div className="row" style={{ marginTop: 8, gap: 6 }}>
        {editing ? (
          <button className="btn" onClick={() => (setEditing(null), setDevice("*"), setTail(""), setLabel(""))}>
            Cancel
          </button>
        ) : null}
        <button className="btn primary" onClick={save} disabled={put.isPending || !proj}>
          {put.isPending ? <Spinner /> : editing ? "Save" : "＋ Add"}
        </button>
      </div>
    </Modal>
  );
}
